/**
 * Which platform strategies trade on this account.
 *
 * The platform's administrator sets them: every account runs the current
 * version of each pool, and a new version reaches the account by itself for
 * new cycles (owner, 2026-10-14). The owner may pause a pool for new cycles;
 * the account's own START still decides whether anything trades. There is no
 * budget: what a strategy commits follows the slot schedule over the balance.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { StatusBadge } from "@/design-system/StatusBadge";
import { activateStrategyAllocation, readAccountRegistry } from "@/shared/api";
import type { RegistryStrategyResponse } from "@/shared/api";



export function AccountAllocations({ tenantId, accountId, roles }: {
  readonly tenantId: string; readonly accountId: string; readonly roles: readonly string[];
}): React.JSX.Element {
  const queries = useQueryClient();
  const key = ["strategy-registry", "account", tenantId, accountId] as const;
  const owner = roles.includes("OWNER");
  const query = useQuery({
    queryKey: key,
    retry: false,
    queryFn: async () => {
      const { data } = await readAccountRegistry();
      if (data === undefined) throw new Error("registry unavailable");
      return data;
    },
  });
  const refresh = async (): Promise<void> => {
    await queries.invalidateQueries({ queryKey: key });
    await queries.invalidateQueries({ queryKey: ["strategy-allocations", tenantId, accountId] });
  };
  const byVersion = new Map<string, RegistryStrategyResponse>((query.data?.versions ?? []).map((item) => [item.version_id, item]));

  const activation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error, response } = await activateStrategyAllocation({ path: { allocation_id: id }, body: { active, command_id: crypto.randomUUID() } });
      if (error !== undefined || response?.ok !== true) {
        const failure: unknown = error;
        const detail = typeof failure === "object" && failure !== null && "detail" in failure && typeof failure.detail === "string" ? failure.detail : "";
        throw new Error(detail === "STRATEGY_NOT_INSTALLED" ? detail : "activation refused");
      }
    },
    onSuccess: refresh,
  });

  const assignments = query.data?.assignments ?? [];
  // One row a pool: the version the platform runs now. Every earlier
  // assignment is history, not a control (owner, 2026-10-30: «полотно»).
  const current = POOLS.flatMap((pool) => {
    const own = assignments.filter((item) => byVersion.get(item.version_id)?.strategy_id === pool);
    const live = own.find((item) => byVersion.get(item.version_id)?.current === true)
      ?? [...own].sort((a, b) => b.assigned_at.localeCompare(a.assigned_at))[0];
    return live === undefined ? [] : [{ pool, item: live }];
  });
  const shown = new Set(current.map(({ item }) => item.allocation_id));
  const history = assignments.filter((item) => !shown.has(item.allocation_id));
  return (
    <Panel title="Стратегии моего счёта" testId="account-allocations">
      <div className="flex flex-col gap-3 text-body-main text-text-secondary">
        <p>Стратегии назначает платформа: на счёте всегда текущая версия каждого пула, новая версия подключается сама. Открытые сделки доживают по своей версии.</p>
        {query.isError ? <p role="alert">Назначения недоступны.</p> : current.length === 0 ? (
          <p>Платформа назначает стратегии в течение минуты после подключения биржи.</p>
        ) : (
          <ul className="divide-y divide-border-subtle" data-testid="allocation-pools">
            {current.map(({ pool, item }) => {
              const version = byVersion.get(item.version_id);
              const capacity = version?.pool?.capacity;
              return (
                <li key={item.allocation_id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-3 first:pt-0" data-testid="allocation-pool">
                  <span className="flex flex-col">
                    <span className="font-medium text-text-primary">{pool}</span>
                    <span className="text-helper-sm text-text-muted">
                      {capacity === undefined ? DESCRIPTION[pool] : `${DESCRIPTION[pool]} · до ${String(capacity)} слотов`}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <StatusBadge tone={item.active_for_new_cycles && capacity !== 0 ? "ready" : "muted"}>
                      {capacity === 0
                        ? "новых сделок нет"
                        : item.active_for_new_cycles ? "новые сделки открываются" : "новые сделки на паузе"}
                    </StatusBadge>
                    {owner && capacity !== 0 && (
                      <Button onClick={() => { activation.mutate({ id: item.allocation_id, active: !item.active_for_new_cycles }); }} disabled={activation.isPending}>
                        {item.active_for_new_cycles ? "Пауза" : "Включить"}
                      </Button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {activation.isError && activation.error.message === "STRATEGY_NOT_INSTALLED" ? (
          <p role="alert" className="text-status-warning">Стратегия ещё устанавливается в ваш счёт — это занимает до минуты. Повторите чуть позже.</p>
        ) : activation.isError && <p role="alert" className="text-status-warning">Действие отклонено. Попробуйте позже.</p>}
        <p className="text-helper-sm text-text-muted">
          Плечо 2x. Число сделок ограничено риском счёта: каждый вход проверяется на падение рынка на 50%.
        </p>
        {history.length > 0 && (
          <details className="text-helper-sm" data-testid="allocation-history">
            <summary className="cursor-pointer text-text-secondary hover:text-text-primary focus-visible:outline-2 focus-visible:outline-focus-ring">
              {`Прежние версии (${String(history.length)})`}
            </summary>
            <ul className="mt-2 flex flex-col gap-1 text-text-muted">
              {history.map((item) => (
                <li key={item.allocation_id}>
                  {`${byVersion.get(item.version_id)?.strategy_id ?? "—"} · назначена ${new Date(item.assigned_at).toLocaleDateString("ru-RU")}`}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Panel>
  );
}

const POOLS = ["PRIME", "PULSE", "FLASH"] as const;

const DESCRIPTION: Readonly<Record<(typeof POOLS)[number], string>> = {
  // The same words as «Помощь» → «Пулы PRIME, PULSE и FLASH».
  PRIME: "основной пул, длинная сетка",
  PULSE: "быстрые сделки, вход по рейтингу",
  FLASH: "выведен: новых сделок нет, открытые доработают",
};
